/* eslint-disable camelcase */
import {IDENTITY_CONFIG} from './utils/authConst'
import {Log, User as OidcUser, UserManager, UserManagerSettings, WebStorageStateStore} from 'oidc-client'
import {AccessRole, IUser, SigninRedirectCallbackReturn} from '../types/AuthTypes'
import {IAuthContext} from '../contexts/AuthContext'

export default class AuthService implements IAuthContext {
  userManager : UserManager

  constructor() {
    const settings : UserManagerSettings = {
      authority: IDENTITY_CONFIG.stsAuthority,
      client_id: IDENTITY_CONFIG.clientId,
      client_secret: IDENTITY_CONFIG.clientSecret,
      redirect_uri: `${IDENTITY_CONFIG.clientRoot}signin-oidc-callback`,
      silent_redirect_uri: `${IDENTITY_CONFIG.clientRoot}silentrenew`,
      post_logout_redirect_uri: `${IDENTITY_CONFIG.clientRoot}`,
      response_type: 'code',
      scope: IDENTITY_CONFIG.clientScope,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      stateStore: new WebStorageStateStore({ store: window.localStorage }),
      automaticSilentRenew: true,
    }

    this.userManager = new UserManager(settings)

    Log.logger = console
    Log.level = Log.WARN

    this.setupEvents()
  }

  setupEvents() {
    this.userManager.events.addUserLoaded((oidcUser) => {
      if (window.location.href.indexOf('signin-oidc-callback') !== -1) {
        this.navigateToScreen()
      }
    })

    this.userManager.events.addSilentRenewError(e => {
      console.error('silent renew error', e)
    })

    this.userManager.events.addAccessTokenExpired(async () => {
      await this.signinSilent()
    })
  }

  clearStaleState = async (): Promise<void> => {
    await this.userManager.clearStaleState()
  }

  getUser = async (): Promise<IUser | null> => {
    const oidcUser = await this.getOidcUser()
    if (oidcUser) return this.createDomainUser(oidcUser)

    await this.signinRedirect().catch(err => console.error(err))
    return null
  }

  parseJwt = (token : string) => {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace('-', '+').replace('_', '/')
    return JSON.parse(window.atob(base64))
  }

  navigateToScreen = () => {
    window.location.replace('/')
  }

  isAuthenticated = async (): Promise<boolean> => {
    const oidcUser = await this.getOidcUser()
    if (oidcUser === null)
      return false

    return !oidcUser.expired
  }

  createDomainUser = (oidcUser: OidcUser): IUser => {
    const defaultAvatar  = 'https://pyxis.nymag.com/v1/imgs/47b/704/17728aba8826057d91e83ad4757786bdcb-14-avatar.2x.rsquare.w330.jpg'
    const accessRoleId : AccessRole = this.judgeAccess(oidcUser)
    return {
      oidcUser: oidcUser,
      avatar: defaultAvatar,
      email: oidcUser.profile.email,
      familyName: oidcUser.profile.family_name,
      givenName: oidcUser.profile.given_name,
      id: oidcUser.profile.sub,
      username: oidcUser.profile.name ?? oidcUser.profile.preferred_username,
      accessRoleId: accessRoleId,
    }
  }

  /**
   * Decides what chrome the user gets — never what data they may see. Plan entitlement is the
   * backend's answer to `GET Plan/{productId}`; nothing here gates a request.
   */
  private judgeAccess(oidcUser: OidcUser): AccessRole {
    if (!oidcUser || !oidcUser.id_token || !oidcUser.access_token || !oidcUser.profile) return AccessRole.NoAccess
    if(oidcUser.expired) return AccessRole.NoAccess

    // ADFS omits a claim entirely rather than sending it empty, so these are read through a guard.
    const idp: string = oidcUser.profile.idp ?? ''
    const amr: string[] = oidcUser.profile.amr ?? []

    // This test has to stay exactly as strict as the API's employee check, `idp` case-sensitivity
    // included. Anything looser offers a client controls the API then answers with a 403.
    if(idp === 'CEM.EmployeeIdentityAdfs'
        && amr.some(method => method.toLowerCase() === 'external')) {
      return AccessRole.CemAdmin
    }

    // Anyone else holding a live token is a client. Whether they can open anything is settled
    // per request by the backend, so an unrecognised `idp` must not lock the app down here.
    return AccessRole.Client
  }

  getOidcUser = async (): Promise<OidcUser | null> => {
    try {
      return await this.userManager.getUser()
    } catch (err) {
      console.error('AuthService.getOidcUser', err)
      return null
    }
  }

  signinSilent = async () => {
    try {
      await this.userManager.signinSilent()
    } catch (err) {
      console.error('AuthService.signinSilent', err)
      await this.signinRedirect()
    }
  }

  signinRedirect = async (returnUrl: string | undefined = undefined): Promise<void> => {
    await this.clearStaleState()

    const signInRedirectArg: any =
        returnUrl === null || returnUrl === undefined
            ? {}
            : {state: {returnUrl}}

    try {
      await this.userManager.signinRedirect(signInRedirectArg)
    } catch (err) {
      console.error('AuthService.signinRedirect', err)
    }
  }

  signinRedirectCallback = async (): Promise<SigninRedirectCallbackReturn | null> => {
    await this.clearStaleState()
    try {
      const oidcUser = await this.userManager.signinRedirectCallback()
      const user = this.createDomainUser(oidcUser)
      const returnUrl = oidcUser.state?.returnUrl ?? '/'
      return {user, returnUrl}
    } catch (err) {
      console.error('AuthService.signinRedirectCallback', err)
      return null
    }
  }

  signinSilentCallback = async (): Promise<IUser | null> => {
    await this.clearStaleState()
    try {
      const oidcUser = await this.userManager.signinSilentCallback()
      if (oidcUser === undefined)
        return null

      return this.createDomainUser(oidcUser)
    } catch (err) {
      console.error('AuthService.signinSilentCallback', err)
      return null
    }
  }

  createSigninRequest = async (): Promise<void> => {
    await this.userManager.createSigninRequest().catch(err => console.error(err))
  }

  logout = async (): Promise<void> => {
    localStorage.clear()
    await this.userManager.signoutRedirect({}).catch(err => console.error(err))
  }

  signoutRedirectCallback = async (): Promise<void> => {
    await this.userManager
        .signoutRedirectCallback()
        .then(() => {
          localStorage.clear()
          window.location.replace(process.env.REACT_APP_PUBLIC_URL ?? 'https://www.cembenchmarking.com/')
        })
        .catch(err => console.error(err))
  }
}
